import { Mountain, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

export default function CostGuidePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="cost-guide-page">
      <div className="border-b border-[#262626] bg-black/60 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-[#A3A3A3] hover:text-white transition-colors"><ArrowLeft className="w-5 h-5" /></button>
          <Mountain className="w-6 h-6 text-[#FF3B30]" />
          <span className="font-heading text-lg tracking-[0.15em] text-white">LNMA COST GUIDE</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-[2px] bg-[#FF3B30]" />
          <span className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3] font-body">Families Cost Guide 2027</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight text-white mb-4">COST <span className="text-[#FF3B30]">GUIDE</span></h1>
        <p className="text-sm text-[#A3A3A3] font-body leading-relaxed max-w-2xl mb-12">Everything you need to know about the financial commitment of joining La Nucia MTB Academy. Early access pricing — subject to change.</p>

        {/* Programme Options */}
        <h2 className="font-heading text-2xl uppercase tracking-wider text-white mb-6">Programme Options</h2>
        <div className="bg-[#111111] border border-[#262626] overflow-hidden mb-12">
          <Table>
            <TableHeader>
              <TableRow className="border-[#262626] hover:bg-transparent bg-[#0A0A0A]">
                <TableHead className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-heading py-4 px-6">Programme</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-heading py-4 px-6">Duration</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-heading py-4 px-6">Price</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="border-[#262626]"><TableCell className="text-white font-body font-semibold py-4 px-6">Full-Year Boarding</TableCell><TableCell className="text-[#A3A3A3] font-body py-4 px-6">10 months</TableCell><TableCell className="font-heading text-xl text-white py-4 px-6">€52,000</TableCell></TableRow>
              <TableRow className="border-[#262626]"><TableCell className="text-white font-body font-semibold py-4 px-6">Semester Boarding</TableCell><TableCell className="text-[#A3A3A3] font-body py-4 px-6">5 months</TableCell><TableCell className="font-heading text-xl text-white py-4 px-6">€28,000</TableCell></TableRow>
              <TableRow className="border-[#262626]"><TableCell className="text-white font-body font-semibold py-4 px-6">Summer Camp Intensive</TableCell><TableCell className="text-[#A3A3A3] font-body py-4 px-6">3 weeks</TableCell><TableCell className="font-heading text-xl text-white py-4 px-6">€3,200</TableCell></TableRow>
              <TableRow className="border-[#262626]"><TableCell className="text-white font-body font-semibold py-4 px-6">Day Rider Monthly</TableCell><TableCell className="text-[#A3A3A3] font-body py-4 px-6">1 month</TableCell><TableCell className="font-heading text-xl text-white py-4 px-6">€1,200</TableCell></TableRow>
            </TableBody>
          </Table>
        </div>

        {/* What's Included */}
        <h2 className="font-heading text-2xl uppercase tracking-wider text-white mb-6">What's Included (Full-Year & Semester)</h2>
        <div className="bg-[#111111] border border-[#262626] p-8 mb-12">
          <ul className="space-y-3">
            {[
              "Furnished room in sports village residence",
              "Full board catering (3 meals/day)",
              "Access to Olympic village facilities (gym, pool, physio)",
              "Choice of accredited online schools — USPA, Athlete Experience US or King's InterHigh — with an in-house academic advisor and in-person teacher supervision",
              "Discipline-specific coaching (XC, DH, Enduro, Freeride)",
              "Gym & S&C programme",
              "Life coach & wellbeing support",
              "Community events & team building",
              "Race competition preparation & coaching",
              "SMART goal & KPI tracking system",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#32D74B] rounded-full mt-2 shrink-0" />
                <span className="text-sm text-[#A3A3A3] font-body">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* What's NOT Included */}
        <h2 className="font-heading text-2xl uppercase tracking-wider text-white mb-6">What's Not Included</h2>
        <div className="bg-[#111111] border border-[#262626] p-8 mb-12">
          <ul className="space-y-3">
            {[
              "Race & event travel costs — quoted trip by trip when arranged; nothing to budget in advance",
              "Personal bike & equipment — students bring their own (on-campus mechanics workshop available)",
              "Online school tuition fees (varies by platform chosen — typically €3,000–€8,000/year)",
              "Travel insurance",
              "Personal spending money",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#FF453A] rounded-full mt-2 shrink-0" />
                <span className="text-sm text-[#A3A3A3] font-body">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Key Notes */}
        <h2 className="font-heading text-2xl uppercase tracking-wider text-white mb-6">Key Notes</h2>
        <div className="bg-[#111111] border border-[#262626] p-8 mb-12">
          <ul className="space-y-4">
            {[
              "Limited to 25 inaugural places",
              "Opening is planned for 2027 and is subject to reaching our minimum inaugural enrolment",
              "All prices are early access and may adjust as the project develops",
              "Payment plans can be discussed — contact us to explore options",
              "Families receive transparent cost breakdowns for every trip/event before committing",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#FF3B30] rounded-full mt-2 shrink-0" />
                <span className="text-sm text-[#A3A3A3] font-body">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="bg-[#111111] border border-[#262626] p-8 text-center">
          <p className="text-sm text-[#A3A3A3] font-body mb-2">Questions about costs?</p>
          <a href="mailto:lnmtbacademy@gmail.com" className="text-[#FF3B30] font-body hover:underline">lnmtbacademy@gmail.com</a>
          <p className="text-xs text-[#555] font-body mt-4">Sebastian Sanchez & Carlos Coloma — Co-founders, La Nucia MTB Academy</p>
        </div>
      </div>
    </div>
  );
}
